import { Duration } from "luxon";

import type { HosClockKind, HosRecapSummary } from "@/types/driverHos";
import { formatZonedDate } from "@/utils/dateFormat";

/**
 * What each clock is called on the block, by the hours each one allows, as drivers and dispatchers name them.
 *
 * The 8-hour clock is **8H Rest**: the driving it allows before the 30-minute break is due, not the break's length.
 * `70H Cycle` assumes the 70-hour week; neither hos-clocks nor the forecast carries a driver's cycle cap.
 */
export const HOS_CLOCK_LABELS: Record<HosClockKind, string> = {
  break: "8H Rest",
  drive: "11H Drive",
  shift: "14H Shift",
  cycle: "70H Cycle",
};

/** Time left on a clock as `h:mm`, rounded down so a clock never reads as having more than it does. */
export function formatHosRemaining(secondsLeft: number): string {
  const { hours, minutes } = wholeHoursAndMinutes(Math.abs(secondsLeft));
  const sign = secondsLeft < 0 ? "-" : "";
  return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

/** Hours coming back as `+8h 15m`. */
function formatGain(gainedSeconds: number): string {
  const { hours, minutes } = wholeHoursAndMinutes(gainedSeconds);
  return `+${hours}h ${String(minutes).padStart(2, "0")}m`;
}

/** A span of seconds as whole hours and the whole minutes past them, the leftover seconds dropped. */
function wholeHoursAndMinutes(seconds: number): { hours: number; minutes: number } {
  const wholeMinutes = Math.floor(Duration.fromObject({ seconds }).as("minutes"));
  const { hours, minutes } = Duration.fromObject({ minutes: wholeMinutes }).shiftTo("hours", "minutes");
  return { hours, minutes };
}

/**
 * The weekday of a bare `YYYY-MM-DD`, read at UTC midnight so the dispatcher's own zone cannot move it a day.
 */
function weekdayOf(date: string): string {
  return formatZonedDate(new Date(`${date}T00:00:00Z`), "utc", "EEE");
}

/** The recap as one line: `Recap · +8h 15m Fri · +11h 00m Sat`; null when the timer carries none. */
export function formatRecap(recap: readonly HosRecapSummary[]): string | null {
  if (recap.length === 0) return null;
  return ["Recap", ...recap.map((day) => `${formatGain(day.gainedSeconds)} ${weekdayOf(day.date)}`)].join(" · ");
}
